import type { ICategoryMenu } from '@/assets/types/api/Categories';
import { EnumProductFlag } from '@/assets/types/api/Categories';
import type { IMenu, ISolutionMenu } from '@/assets/types/api/Menus';

enum EnumMenuIndex {
  PRODUCTS = 1,
  SOLUTIONS = 2,
  TOPICS = 3,
  ECOSYSTEM = 4,
  RESOURCES = 5,
}

// TODO: 待 CN 選單確定改版後移除
enum EnumCNMenuIndex {
  SOLUTIONS = 0,
  PRODUCTS = 1,
  RESOURCES = 2,
}

interface IFormatMenu {
  code?: string;
  name: string;
  type: number;
  shortIntroduction: string | null;
  link: {
    url: string;
    target: number;
  } | null;
  isHot: boolean;
  isNew?: boolean;
  isSuite?: boolean;
  subMenus: IFormatMenu[];
}

interface IFormatProductMenu extends IFormatMenu {
  groupCode: string | null;
  level: 'group' | 'category' | 'collection' | 'product';
  subMenus: IFormatProductMenu[];
}

export const useMenuStore = defineStore('menuStore', () => {
  const nuxtApp = useNuxtApp();
  const { isTablet } = useScreen();

  /* State */

  const menus = ref<IMenu[]>([]);
  const solutionMenu = ref<ISolutionMenu[]>([]);
  const productsMenu = ref<ICategoryMenu[]>([]);
  const currentMenu = ref<number | null>(null);
  const currentMobileMenu = ref<string | null>(null);
  const isMenuOpen = ref<boolean>(false);

  /* Getters */

  const menuName = computed(
    (): {
      SOLUTIONS: string;
      PRODUCTS: string;
      RESOURCES: string;
      TOPICS: string;
      ECOSYSTEM: string;
      // TODO: 待 CN 選單確定改版後移除
      CN_SOLUTIONS: string;
      CN_PRODUCTS: string;
      CN_RESOURCES: string;
    } => {
      return {
        PRODUCTS: menus.value[EnumMenuIndex.PRODUCTS]?.name || 'Products',
        SOLUTIONS: menus.value[EnumMenuIndex.SOLUTIONS]?.name || 'Solutions',
        TOPICS: menus.value[EnumMenuIndex.TOPICS]?.name || 'Topics',
        ECOSYSTEM: menus.value[EnumMenuIndex.ECOSYSTEM]?.name || 'Ecosystem',
        RESOURCES: menus.value[EnumMenuIndex.RESOURCES]?.name || 'Resources',

        CN_SOLUTIONS:
          menus.value[EnumCNMenuIndex.SOLUTIONS]?.name || 'Solutions',
        CN_PRODUCTS: menus.value[EnumCNMenuIndex.PRODUCTS]?.name || 'Products',
        CN_RESOURCES:
          menus.value[EnumCNMenuIndex.RESOURCES]?.name || 'Resources',
      };
    }
  );

  const formatMenuByIndex = computed(() => {
    return (index: number): IFormatMenu[] => {
      const { $i18n } = nuxtApp;
      const { locale } = $i18n;

      const processFormatItem = (payload: IMenu): IFormatMenu => {
        const { name, type } = payload;

        const link = (() => {
          if (payload.link) {
            const { url, target } = payload.link;
            if (target !== 1) {
              return { url: `/${locale.value}/marketplace${url}`, target };
            }
            return { url, target };
          }
          return null;
        })();

        const subMenus = payload.subMenus.map((item: IMenu) =>
          processFormatItem(item)
        );

        return {
          name,
          type,
          link,
          shortIntroduction: null,
          isHot: false,
          isNew: false,
          isSuite: false,
          subMenus,
        };
      };

      return (
        menus.value[index]?.subMenus.map((item: IMenu) =>
          processFormatItem(item)
        ) || []
      );
    };
  });

  const formatProductsMenu = computed((): IFormatProductMenu[] => {
    const { $i18n } = nuxtApp;
    const { locale, t } = $i18n;

    const processProductsList = (
      products: ICategoryMenu['products']
    ): IFormatProductMenu[] => {
      return products.map((item) => {
        return {
          name: item.name,
          type: 1,
          shortIntroduction: item.description,
          link: {
            url: `/${locale.value}/marketplace/products/${item.alias}`,
            target: 0,
          },
          isHot: item.flag === EnumProductFlag.HOT,
          isNew: item.flag === EnumProductFlag.NEW,
          isSuite: item.flag === EnumProductFlag.SUITE,
          subMenus: [],
          groupCode: null,
          level: 'product',
        };
      });
    };

    const processCollectionsList = (
      collections: ICategoryMenu[]
    ): IFormatProductMenu[] => {
      return collections.map(({ code, products }) => {
        return {
          code,
          name: t(`menu.product.collection.${code}`),
          type: 1,
          shortIntroduction: '',
          link: null,
          isHot: false,
          isNew: false,
          isSuite: false,
          subMenus: processProductsList(products),
          groupCode: null,
          level: 'collection',
        };
      });
    };

    const processCategoryList = (
      payload: ICategoryMenu
    ): IFormatProductMenu => {
      const { code, subCategories } = payload;

      // partner-applications 和 expert-services 要直接連到此 Category 頁面，不會有下一層
      const subMenus = (() => {
        if (code === 'partner-applications' || code === 'expert-services') {
          return [];
        }
        return processCollectionsList(subCategories);
      })();

      return {
        code,
        name: t(`menu.product.category.${code}`),
        type: 2,
        shortIntroduction: null,
        link: {
          url: `/${locale.value}/marketplace/category/${code}`,
          target: 0,
        },
        isHot: false,
        isNew: false,
        isSuite: false,
        subMenus,
        groupCode: null,
        level: 'category',
      };
    };

    return productsMenu.value.map((item: ICategoryMenu) =>
      processCategoryList(item)
    );
  });

  const formatProductGroupMenu = computed((): IFormatProductMenu[] => {
    return formatProductsMenu.value;
  });

  const formatSolutionsMenu = computed((): IFormatMenu[] => {
    const processFormatItem = (payload: ISolutionMenu): IFormatMenu => {
      const subMenus = payload.subMenus.map((item) => processFormatItem(item));
      return { ...payload, isNew: false, isSuite: false, subMenus };
    };
    return solutionMenu.value.map((item) => processFormatItem(item));
  });

  const formatTopicsMenu = computed((): IFormatMenu[] => {
    return formatMenuByIndex.value(EnumMenuIndex.TOPICS);
  });

  const formatEcosystemMenu = computed((): IFormatMenu[] => {
    return formatMenuByIndex.value(EnumMenuIndex.ECOSYSTEM);
  });

  const formatResourcesMenu = computed((): IFormatMenu[] => {
    return formatMenuByIndex.value(EnumMenuIndex.RESOURCES);
  });

  // TODO: 待 CN 選單確定改版後移除
  const formatCNResourcesMenu = computed((): IFormatMenu[] => {
    return formatMenuByIndex.value(EnumCNMenuIndex.RESOURCES);
  });

  /* Actions */

  const USE_GET_MENU = async () => {
    /**
     * 取得選單項目
     */

    const { $i18n, $notification } = nuxtApp;
    const { locale } = $i18n;

    return await useCustomFetch(
      () => `/marketplace/api/v1/menus?websiteCode=${locale.value}`,
      {
        method: 'GET',
        lazy: true,
        immediate: true,
        onResponse: ({ response }) => {
          const { _data, status } = response;

          if (status >= 200 && status < 300) {
            menus.value = [...(_data as IMenu[])];
          } else {
            console.error(_data);
            $notification({
              message: (_data as { message: string }).message,
              type: 'error',
            });
          }
        },
      }
    );
  };

  const USE_GET_SOLUTION_MENU = async () => {
    /**
     * 取得解決方案選單項目
     */

    const { $i18n, $notification } = nuxtApp;
    const { locale } = $i18n;

    return await useCustomFetch(
      () => `/marketplace/api/v1/menus/solutions?websiteCode=${locale.value}`,
      {
        method: 'GET',
        lazy: true,
        immediate: true,
        onResponse: ({ response }) => {
          const { _data, status } = response;

          if (status >= 200 && status < 300) {
            solutionMenu.value = [...(_data as ISolutionMenu[])];
          } else {
            console.error(_data);
            $notification({
              message: (_data as { message: string }).message,
              type: 'error',
            });
          }
        },
      }
    );
  };

  const USE_GET_PRODUCTS_MENU = async () => {
    /**
     * 取得所有產品分類及其底下的所有產品
     */
    const { $i18n, $notification } = nuxtApp;
    const { locale } = $i18n;

    return await useCustomFetch(
      () =>
        `/marketplace/api/v1/product-categories?websiteCode=${locale.value}`,
      {
        method: 'GET',
        lazy: true,
        immediate: true,
        onResponse: ({ response }) => {
          const { _data, status } = response;

          if (status >= 200 && status < 300) {
            productsMenu.value = [...(_data as ICategoryMenu[])];
          } else {
            console.error(_data);
            $notification({
              message: (_data as { message: string }).message,
              type: 'error',
            });
          }
        },
      }
    );
  };

  const HANDLE_CLOSE_MENU = (): void => {
    /**
     * 關閉 MENU 視窗，但是在 Tablet 保留當前切換的 menu 選單
     */
    isMenuOpen.value = false;

    if (!isTablet.value) {
      currentMenu.value = null;
    }
  };

  return {
    menus,
    solutionMenu,
    productsMenu,
    currentMenu,
    currentMobileMenu,
    isMenuOpen,
    menuName,
    formatMenuByIndex,
    formatProductsMenu,
    formatProductGroupMenu,
    formatSolutionsMenu,
    formatTopicsMenu,
    formatEcosystemMenu,
    formatResourcesMenu,
    formatCNResourcesMenu,
    USE_GET_MENU,
    USE_GET_SOLUTION_MENU,
    USE_GET_PRODUCTS_MENU,
    HANDLE_CLOSE_MENU,
  };
});

export { EnumCNMenuIndex, EnumMenuIndex };
export type { IFormatMenu, IFormatProductMenu };
